import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, refreshSession, setAccessToken, setSessionLostHandler } from '../api/client';

export interface Me {
  id: string;
  username: string;
  email: string;
  fullName: string;
}

interface AuthState {
  status: 'loading' | 'anonymous' | 'authenticated';
  me: Me | null;
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [me, setMe] = useState<Me | null>(null);

  const loadMe = useCallback(async () => {
    const m = await api<Me>('/me');
    setMe(m);
    setStatus('authenticated');
  }, []);

  // restore the session from the refresh cookie
  useEffect(() => {
    setSessionLostHandler(() => {
      setMe(null);
      setStatus('anonymous');
      qc.clear();
    });
    refreshSession().then((ok) => (ok ? loadMe().catch(() => setStatus('anonymous')) : setStatus('anonymous')));
  }, [loadMe, qc]);

  const login = useCallback(
    async (login: string, password: string) => {
      const r = await api<{ accessToken: string }>('/auth/login', { method: 'POST', body: { login, password } });
      setAccessToken(r.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST', body: {} }).catch(() => undefined);
    setAccessToken(null);
    setMe(null);
    setStatus('anonymous');
    qc.clear();
  }, [qc]);

  const value = useMemo(() => ({ status, me, login, logout }), [status, me, login, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
